/**
 * moka.js — Moka United ödeme linki + tahsilat eşleştirme kuyruğu, React Query hook'ları.
 *
 * Backend referansı: backend/routes/moka_links.py (bkz. worktree
 * .claude/worktrees/moka-odeme-linki — bu turda backend DEĞİŞTİRİLMEDİ, salt okundu).
 * Tüm uçlar admin JWT gerektirir, `/api` prefix'i apiClient baseURL'inde zaten var.
 *
 * Hooks:
 *   useMokaConfig()            — GET /admin/moka/config (bayrak/kimlik özeti)
 *   useMokaLinks(params, opts) — GET /admin/moka/links (installment_id/user_id/status filtresi)
 *   useCreateMokaLink()        — POST /admin/moka/links
 *   useResendMokaLink()        — POST /admin/moka/links/{link_id}/resend
 *   useCancelMokaLink()        — POST /admin/moka/links/{link_id}/cancel
 *   useMokaQueue(params)       — GET /admin/moka/queue (match_status filtresi)
 *   useApproveMokaQueueItem()  — POST /admin/moka/queue/{dealer_payment_id}/approve
 *   useRejectMokaQueueItem()   — POST /admin/moka/queue/{dealer_payment_id}/reject
 *   useMokaSyncOnOpen(userId)  — Faz M (2026-09-28, feature/moka-profil-link):
 *                                 POST /admin/moka/sync'i sayfa açılınca (mount)
 *                                 BİR KEZ sessizce çağırır — periyodik scheduler
 *                                 YERİNE. Bkz. fonksiyon docstring'i.
 *
 * NOT (backend sözleşmesi — bkz. backend worktree commit db0cc00): 409
 * `moka_link_zaten_aktif` yanıtı artık `detail.existing_link` alanında mevcut
 * aktif linkin güvenli özetini taşır (`link_id, status, amount, created_at,
 * created_by, payment_page_url, delivery` — `code_for_hash` HARİÇ, beyaz
 * liste). `MokaLinkDialog` yine de yalnızca 409'a GÜVENMEZ: dialog açılışında
 * `useMokaLinks({ installmentId, status: 'active' })` ile aktif linki
 * ÖNCEDEN sorgular (proaktif kontrol) — 409 + `existing_link`, buna rağmen
 * oluşan yarış durumunda (başka bir admin araya girdiyse) devreye giren
 * ikincil bir yakalama katmanıdır, `existing_link` gelmezse eski toast
 * davranışına düşülür (geriye uyumluluk).
 */

import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { studentKeys } from '@/api/queries/students';

export const mokaKeys = {
  config: ['admin', 'moka', 'config'],
  links: (params) => ['admin', 'moka', 'links', params || {}],
  queue: (params) => ['admin', 'moka', 'queue', params || {}],
};

// ── useMokaConfig ────────────────────────────────────────────────────────────

/** `{ link_enabled, polling_enabled, has_credentials, link_active, polling_active }` */
export function useMokaConfig() {
  const { authHeaders } = useAuth();
  return useQuery({
    queryKey: mokaKeys.config,
    queryFn: async () => {
      const res = await apiClient.get('/admin/moka/config', { headers: authHeaders() });
      return res.data;
    },
    staleTime: 60_000,
  });
}

// ── useMokaLinks ──────────────────────────────────────────────────────────────

/**
 * @param {{installmentId?: string, userId?: string, status?: string, skip?: number, limit?: number}} params
 * @param {import('@tanstack/react-query').UseQueryOptions} options — ör. `{ enabled }`
 * @returns `{ links: [], total, skip, limit }`
 */
export function useMokaLinks(params = {}, options = {}) {
  const { authHeaders } = useAuth();
  const { installmentId, userId, status, skip = 0, limit = 20 } = params;
  const queryParams = { installment_id: installmentId, user_id: userId, status, skip, limit };
  return useQuery({
    queryKey: mokaKeys.links(queryParams),
    queryFn: async () => {
      const res = await apiClient.get('/admin/moka/links', { headers: authHeaders(), params: queryParams });
      return res.data || { links: [], total: 0, skip, limit };
    },
    ...options,
  });
}

// ── Link mutasyonları ─────────────────────────────────────────────────────────

/** `mutateAsync({installment_id, bank_id, voice_approval_confirmed, voice_approval_note?, force?})` */
export function useCreateMokaLink() {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      apiClient.post('/admin/moka/links', payload, { headers: authHeaders() }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'moka', 'links'] });
    },
  });
}

/** `mutateAsync(linkId)` */
export function useResendMokaLink() {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId) =>
      apiClient.post(`/admin/moka/links/${linkId}/resend`, {}, { headers: authHeaders() }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'moka', 'links'] });
    },
  });
}

/** `mutateAsync(linkId)` */
export function useCancelMokaLink() {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId) =>
      apiClient.post(`/admin/moka/links/${linkId}/cancel`, {}, { headers: authHeaders() }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'moka', 'links'] });
    },
  });
}

// ── Eşleştirme kuyruğu ────────────────────────────────────────────────────────

/**
 * @param {{matchStatus?: string, skip?: number, limit?: number}} params — `matchStatus`
 * boşsa backend varsayılan olarak `queued` + `unmatched_link_not_found` döner.
 * @returns `{ queue: [], total, skip, limit }`
 */
export function useMokaQueue(params = {}) {
  const { authHeaders } = useAuth();
  const { matchStatus, skip = 0, limit = 20 } = params;
  const queryParams = { match_status: matchStatus, skip, limit };
  return useQuery({
    queryKey: mokaKeys.queue(queryParams),
    queryFn: async () => {
      const res = await apiClient.get('/admin/moka/queue', { headers: authHeaders(), params: queryParams });
      return res.data || { queue: [], total: 0, skip, limit };
    },
  });
}

/** `mutateAsync({ dealerPaymentId, installmentId, bankId })` */
export function useApproveMokaQueueItem() {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealerPaymentId, installmentId, bankId }) =>
      apiClient
        .post(
          `/admin/moka/queue/${dealerPaymentId}/approve`,
          { installment_id: installmentId, bank_id: bankId },
          { headers: authHeaders() },
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'moka', 'queue'] });
    },
  });
}

/** `mutateAsync({ dealerPaymentId, note? })` */
export function useRejectMokaQueueItem() {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dealerPaymentId, note }) =>
      apiClient
        .post(`/admin/moka/queue/${dealerPaymentId}/reject`, note ? { note } : {}, { headers: authHeaders() })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'moka', 'queue'] });
    },
  });
}

// ── useMokaSyncOnOpen (Faz M — periyodik DEĞİL, sayfa açılınca eşitleme) ──────

/**
 * Faz M (2026-09-28, feature/moka-profil-link) — Moka ödeme verisi artık
 * `scheduler.py`'de 5 dakikada bir OTOMATİK çekilmiyor
 * (`MOKA_POLLING_AUTORUN_ENABLED` varsayılan KAPALI, bkz. backend
 * `services/moka/config.py::is_autorun_enabled` docstring'i). Bunun yerine
 * bu hook, `userId` verildiğinde (öğrenci Muhasebe sekmesi açıldığında,
 * `AccountingTab/index.jsx`'te çağrılır) BİR KEZ sessizce
 * `POST /admin/moka/sync`'i tetikler — mevcut `services/moka/jobs.py::
 * run_polling_job`'ı (idempotent, CAS kilitli) çağırır, başarılıysa bu
 * öğrencinin ledger sorgusunu (`useStudentLedger`) invalidate eder ki yeni
 * eşleşen bir tahsilat varsa ekran otomatik güncellensin.
 *
 * Hata SESSİZ yutulur (toast YOK, konsola da YAZILMAZ) — bu çağrı isteğe
 * bağlı bir iyileştirme, ledger kendi ayrı sorgusuyla zaten normal çalışır;
 * Moka entegrasyonu kapalıyken (`MOKA_POLLING_ENABLED=false`) backend zaten
 * `{"status": "skipped", "reason": "disabled"}` döner — bu da sorunsuz kabul
 * edilir (hata DEĞİL).
 *
 * `AdminMuhasebeListesi.jsx` (muhasebe listesi sayfası) BU TURDA bağlanmadı —
 * başka bir agent'ın dosyası (feature/muhasebe-listesi-web). Aynı desenle
 * bağlanabilir: sayfa mount olduğunda (ör. `useEffect`) her satırın
 * `user_id`'si YERİNE, o sayfa muhtemelen TEK bir toplu senkronizasyon
 * ister — bu durumda `userId` parametresi olmadan (`userId` opsiyonel
 * bırakılabilir) doğrudan `POST /admin/moka/sync`'i bir kez çağırıp genel
 * `['admin','moka']` ailesini invalidate eden ayrı, daha genel bir hook
 * (`useMokaSyncOnOpen(null)` bu haliyle YETERSIZ — `userId` yoksa hangi
 * query'nin invalidate edileceği belli değil) yazılmalı.
 */
export function useMokaSyncOnOpen(userId) {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();
  const firedForRef = useRef(null);

  useEffect(() => {
    if (!userId || firedForRef.current === userId) return;
    firedForRef.current = userId;
    apiClient
      .post('/admin/moka/sync', {}, { headers: authHeaders() })
      .then(() => {
        qc.invalidateQueries({ queryKey: studentKeys.ledger(userId) });
      })
      .catch(() => {
        // Faz M — sessiz hata: bkz. fonksiyon docstring'i.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
}
